// Sentry error tracking initialization
// Lazy-loaded: @sentry/react is ~150KB, so we dynamically import it after first render

import type * as SentryTypes from '@sentry/react';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

let SentryModule: typeof SentryTypes | null = null;
let initPromise: Promise<typeof SentryTypes | null> | null = null;

// Queue errors that occur before Sentry loads
let errorQueue: Array<{ error: Error; context?: Record<string, unknown> }> = [];

export function initSentry(): Promise<typeof SentryTypes | null> {
  if (!SENTRY_DSN) {
    if (import.meta.env.PROD) {
      console.warn('[Sentry] VITE_SENTRY_DSN not set — error tracking disabled');
    }
    return Promise.resolve(null);
  }

  if (initPromise) return initPromise;

  initPromise = import('@sentry/react').then((Sentry) => {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: import.meta.env.MODE,
      release: `life-rescripted@${__APP_VERSION__}`,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      // Performance monitoring — sample 10% of transactions in prod
      tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
      // Session replay — capture 1% of sessions, 100% of error sessions
      replaysSessionSampleRate: 0.01,
      replaysOnErrorSampleRate: 1.0,
      // Only send errors from our domain
      allowUrls: [/liferescripted\.net/, /localhost/],
      // Filter out noisy browser extension errors
      ignoreErrors: [
        'ResizeObserver loop',
        'Non-Error promise rejection captured',
        /extensions\//i,
        /^chrome:\/\//i,
      ],
    });

    SentryModule = Sentry;

    // Flush any queued errors
    for (const { error, context } of errorQueue) {
      if (context) {
        Sentry.withScope((scope) => {
          scope.setExtras(context);
          Sentry.captureException(error);
        });
      } else {
        Sentry.captureException(error);
      }
    }
    errorQueue = [];

    return Sentry;
  });

  return initPromise;
}

/**
 * Report an error to Sentry with optional context.
 * Safe to call even when Sentry is not yet loaded — errors are queued.
 */
export function captureError(error: Error, context?: Record<string, unknown>) {
  if (!SENTRY_DSN) return;

  if (SentryModule) {
    if (context) {
      SentryModule.withScope((scope) => {
        scope.setExtras(context);
        SentryModule?.captureException(error);
      });
    } else {
      SentryModule.captureException(error);
    }
  } else {
    // Queue until Sentry loads
    errorQueue.push({ error, context });
  }
}

/**
 * Set user context for Sentry (call on login).
 */
export function setSentryUser(userId: string) {
  SentryModule?.setUser({ id: userId });
}

/**
 * Clear user context (call on logout).
 */
export function clearSentryUser() {
  SentryModule?.setUser(null);
}

// Re-export for components that need Sentry types
export { SentryModule as Sentry };
